/**
 * Fishbone (Ishikawa) diagrams from an indented outline.
 *
 * The first line is the effect — the problem, written as the head of the fish. Lines
 * indented under it are the categories (the large bones); lines under a category are its
 * causes (the small bones), and anything deeper is a sub-cause. Categories alternate
 * above and below the spine, left to right, and each category owns a column of the page.
 *
 * Nothing overlaps by construction. A column holds one category box, one slanted bone and
 * a stack of cause rows; every row is a text block sitting on a horizontal twig that runs
 * left from the bone. The text is left-aligned at a common edge chosen so that the widest
 * row ends before the bone at the nearest point of its own band, and each slot of the
 * spine is as wide as the wider of its two columns. Columns on the same side therefore
 * never share an x range, and the spine, head and boxes sit in clear space of their own.
 */

import { LINE_HEIGHT, type Rect } from "@/lib/tool-kit/charts/layout";
import {
  estimateTextWidth,
  maxTextWidth,
  type TextStyle,
  wrapText,
} from "@/lib/tool-kit/charts/text";
import type { OutlineNode } from "@/lib/tool-kit/diagrams/mind-map";
import { parseOutline } from "@/lib/tool-kit/diagrams/mind-map";

/* ------------------------------------------------------------------- parse */

export interface FishCause {
  text: string;
  /** 0 for a cause, 1 for a sub-cause (anything nested deeper is shown at level 1). */
  level: 0 | 1;
  line: number;
}

export interface FishCategory {
  name: string;
  line: number;
  causes: FishCause[];
}

export type FishWarningKind = "tooManyCategories" | "tooManyCauses" | "dropped";

export interface FishWarning {
  kind: FishWarningKind;
  /** The category involved, or empty. */
  text: string;
}

export interface FishParse {
  effect: string;
  categories: FishCategory[];
  warnings: FishWarning[];
}

export const MAX_CATEGORIES = 10;
export const MAX_CAUSES = 14;
const OUTLINE_LIMIT = 400;

/** The English words that may label the first line; they are always recognised. */
const EFFECT_PREFIX_WORDS: readonly string[] = ["effect", "problem", "head"];
const REGEX_SPECIALS = /[.*+?^${}()|[\]\\]/g;
const TRAILING_SPACE = /\s+$/;
const TRAILING_COLON = /\s*:\s*$/;
const WORD_TRAILING_COLON = /\s*[:：]\s*$/;

const prefixPatterns = new Map<string, RegExp>();

/**
 * The pattern for a label such as "Effect:" at the start of the first line: one of the
 * English words or the given localized ones, then an ASCII or full-width colon. Built once
 * per distinct word list.
 */
function effectPrefixPattern(extra: readonly string[]): RegExp {
  const words = [...EFFECT_PREFIX_WORDS, ...extra]
    .map((word) => word.trim().replace(WORD_TRAILING_COLON, ""))
    .filter((word) => word !== "");
  const key = words.join("\u0000");
  const cached = prefixPatterns.get(key);
  if (cached) {
    return cached;
  }
  const alternatives = words
    .map((word) => word.replace(REGEX_SPECIALS, "\\$&"))
    .sort((a, b) => b.length - a.length)
    .join("|");
  const pattern = new RegExp(`^(?:${alternatives})\\s*[:：]\\s*`, "i");
  prefixPatterns.set(key, pattern);
  return pattern;
}

function flatten(node: OutlineNode, level: 0 | 1, into: FishCause[]): void {
  into.push({ text: node.text, level, line: node.line });
  for (const child of node.children) {
    flatten(child, 1, into);
  }
}

/**
 * Read the outline: effect, categories, causes. Beyond the limits, lines are left out.
 * A leading "Effect:", "Problem:" or "Head:" on the first line is dropped, and so is a
 * label in `effectPrefixes` (the page language's word for the effect), with ":" or "：".
 */
export function parseFishbone(
  text: string,
  effectPrefixes: readonly string[] = []
): FishParse {
  const outline = parseOutline(text, OUTLINE_LIMIT);
  const warnings: FishWarning[] = [];
  if (!outline.root) {
    return { effect: "", categories: [], warnings };
  }
  const effect = outline.root.text
    .replace(effectPrefixPattern(effectPrefixes), "")
    .trim();
  const categories: FishCategory[] = [];
  for (const [index, node] of outline.root.children.entries()) {
    const name = node.text.replace(TRAILING_COLON, "").trim();
    if (index >= MAX_CATEGORIES) {
      warnings.push({ kind: "tooManyCategories", text: name });
      continue;
    }
    const all: FishCause[] = [];
    for (const child of node.children) {
      flatten(child, 0, all);
    }
    if (all.length > MAX_CAUSES) {
      warnings.push({ kind: "tooManyCauses", text: name });
    }
    categories.push({
      name,
      line: node.line,
      causes: all.slice(0, MAX_CAUSES),
    });
  }
  if (outline.dropped > 0) {
    warnings.push({ kind: "dropped", text: "" });
  }
  return { effect, categories, warnings };
}

export function fishCounts(parse: FishParse): {
  categories: number;
  causes: number;
  subCauses: number;
} {
  let causes = 0;
  let subCauses = 0;
  for (const category of parse.categories) {
    for (const cause of category.causes) {
      if (cause.level === 0) {
        causes += 1;
      } else {
        subCauses += 1;
      }
    }
  }
  return { categories: parse.categories.length, causes, subCauses };
}

/** Add the named categories that the outline does not already have, after the effect. */
export function addCategories(
  text: string,
  names: readonly string[],
  effectPlaceholder: string,
  effectPrefixes: readonly string[] = []
): string {
  const parse = parseFishbone(text, effectPrefixes);
  const have = new Set(
    parse.categories.map((category) => category.name.toLowerCase())
  );
  const fresh = names.filter((name) => !have.has(name.toLowerCase()));
  const base =
    text.trim() === "" ? effectPlaceholder : text.replace(TRAILING_SPACE, "");
  return fresh.length === 0 ? base : `${base}\n${fresh.join("\n")}`;
}

/* ------------------------------------------------------------------ layout */

export const CATEGORY_FONT = 13;
export const CAUSE_FONT = 12;
export const SUB_FONT = 11;
export const HEAD_FONT = 14;
const CATEGORY_STYLE: TextStyle = { fontSize: CATEGORY_FONT, bold: true };
const HEAD_STYLE: TextStyle = { fontSize: HEAD_FONT, bold: true };

const MARGIN = 16;
/** Horizontal run of the bone per unit of its height. */
const SLANT = 0.3;
const NEAR_PAD = 12;
const FAR_PAD = 10;
const SLOT_GAP = 18;
const TEXT_GAP = 8;
const ROW_PAD = 6;
const SUB_INDENT = 14;
const CAUSE_WRAP = 150;
const SUB_WRAP = 136;
const BOX_PAD_X = 12;
const BOX_PAD_Y = 8;
const HEAD_WRAP = 150;

export type FishSide = "top" | "bottom";

export interface PlacedRow {
  cause: FishCause;
  lines: string[];
  fontSize: number;
  /** Left edge of the text. */
  textX: number;
  /** Top of the text's first line. */
  textY: number;
  /** The text's bounding box. */
  rect: Rect;
  /** The twig under the text, from its start to the bone. */
  twig: { x1: number; x2: number; y: number };
}

export interface PlacedColumn {
  category: FishCategory;
  index: number;
  side: FishSide;
  lines: string[];
  box: Rect;
  /** The bone from its outer end to the spine, as `[x1, y1, x2, y2]`. */
  bone: [number, number, number, number];
  rows: PlacedRow[];
}

export interface FishLayout {
  width: number;
  height: number;
  spineY: number;
  spineStart: number;
  /** Where the spine meets the head. */
  spineEnd: number;
  head: Rect;
  headLines: string[];
  columns: PlacedColumn[];
}

interface Sized {
  category: FishCategory;
  index: number;
  lines: string[];
  boxWidth: number;
  boxHeight: number;
  rows: {
    cause: FishCause;
    lines: string[];
    fontSize: number;
    width: number;
    height: number;
    indent: number;
  }[];
  /** Vertical length of the bone, from its outer end to the spine. */
  need: number;
  textWidth: number;
}

function sizeColumn(category: FishCategory, index: number): Sized {
  const lines = wrapText(category.name || " ", 130, CATEGORY_STYLE, 2);
  const boxWidth = Math.max(
    64,
    Math.ceil(maxTextWidth(lines, CATEGORY_STYLE)) + BOX_PAD_X * 2
  );
  const boxHeight =
    Math.ceil(lines.length * CATEGORY_FONT * LINE_HEIGHT) + BOX_PAD_Y * 2;
  const rows = category.causes.map((cause) => {
    const sub = cause.level === 1;
    const fontSize = sub ? SUB_FONT : CAUSE_FONT;
    const style: TextStyle = { fontSize };
    const wrapped = wrapText(cause.text, sub ? SUB_WRAP : CAUSE_WRAP, style, 3);
    const indent = sub ? SUB_INDENT : 0;
    return {
      cause,
      lines: wrapped,
      fontSize,
      width: Math.ceil(maxTextWidth(wrapped, style)) + indent,
      height: Math.ceil(wrapped.length * fontSize * LINE_HEIGHT) + ROW_PAD,
      indent,
    };
  });
  const stack = rows.reduce((sum, row) => sum + row.height, 0);
  return {
    category,
    index,
    lines,
    boxWidth,
    boxHeight,
    rows,
    need: stack + NEAR_PAD + FAR_PAD,
    textWidth: rows.reduce((widest, row) => Math.max(widest, row.width), 0),
  };
}

interface Placed {
  column: PlacedColumn;
  /** Extent left of / right of the join with the spine, relative to it. */
  left: number;
  right: number;
}

/**
 * Place one column with its join to the spine at x = 0, the spine at `spineY`, and the
 * bone's outer end at `spineY ∓ length`. Returns the column in those local coordinates.
 */
function placeColumn(
  sized: Sized,
  side: FishSide,
  spineY: number,
  length: number
): Placed {
  const sign = side === "top" ? -1 : 1;
  const outerY = spineY + sign * length;
  const outerX = -SLANT * length;
  const boneX = (y: number) =>
    side === "top" ? outerX + SLANT * (y - outerY) : -SLANT * (y - spineY);

  // Rows in reading order, top to bottom of the page.
  const bands: { top: number; height: number }[] = [];
  let cursor = side === "top" ? outerY + FAR_PAD : spineY + NEAR_PAD;
  for (const row of sized.rows) {
    bands.push({ top: cursor, height: row.height });
    cursor += row.height;
  }
  const nearest =
    sized.rows.length === 0
      ? 0
      : Math.min(
          ...bands.map((band, i) =>
            side === "top"
              ? boneX(band.top)
              : boneX(band.top + sized.rows[i].height)
          )
        );
  const textLeft = nearest - sized.textWidth - TEXT_GAP;
  const rows: PlacedRow[] = sized.rows.map((row, i) => {
    const band = bands[i];
    const twigY = band.top + row.height;
    const x = textLeft + row.indent;
    const textHeight = row.lines.length * row.fontSize * LINE_HEIGHT;
    return {
      cause: row.cause,
      lines: row.lines,
      fontSize: row.fontSize,
      textX: x,
      textY: band.top + 1,
      rect: {
        x,
        y: band.top + 1,
        width: row.width - row.indent,
        height: textHeight,
      },
      twig: { x1: x - 2, x2: boneX(twigY), y: twigY },
    };
  });
  const boxY = side === "top" ? outerY - sized.boxHeight : outerY;
  const box: Rect = {
    x: outerX - sized.boxWidth / 2,
    y: boxY,
    width: sized.boxWidth,
    height: sized.boxHeight,
  };
  const left = Math.min(box.x, rows.length > 0 ? textLeft : box.x);
  const right = Math.max(0, box.x + box.width);
  const boneStart: [number, number] = [outerX, outerY];
  return {
    column: {
      category: sized.category,
      index: sized.index,
      side,
      lines: sized.lines,
      box,
      bone:
        side === "top"
          ? [boneStart[0], boneStart[1], 0, spineY]
          : [0, spineY, boneStart[0], boneStart[1]],
      rows,
    },
    left,
    right,
  };
}

function shift(column: PlacedColumn, dx: number, dy: number): PlacedColumn {
  return {
    ...column,
    box: { ...column.box, x: column.box.x + dx, y: column.box.y + dy },
    bone: [
      column.bone[0] + dx,
      column.bone[1] + dy,
      column.bone[2] + dx,
      column.bone[3] + dy,
    ],
    rows: column.rows.map((row) => ({
      ...row,
      textX: row.textX + dx,
      textY: row.textY + dy,
      rect: { ...row.rect, x: row.rect.x + dx, y: row.rect.y + dy },
      twig: {
        x1: row.twig.x1 + dx,
        x2: row.twig.x2 + dx,
        y: row.twig.y + dy,
      },
    })),
  };
}

/** Lay the fish out. Categories alternate above and below the spine, top first. */
export function layoutFishbone(parse: FishParse): FishLayout {
  const sized = parse.categories.map((category, index) =>
    sizeColumn(category, index)
  );
  const tops = sized.filter((_, i) => i % 2 === 0);
  const bottoms = sized.filter((_, i) => i % 2 === 1);
  const topLength = Math.max(0, ...tops.map((column) => column.need));
  const bottomLength = Math.max(0, ...bottoms.map((column) => column.need));
  const topExtent = Math.max(...tops.map((c) => c.boxHeight), 0) + topLength;
  const spineY = MARGIN + Math.max(topExtent, 40);

  // Local placement of every column, then slot positions left to right.
  const local = new Map<number, Placed>();
  for (const column of sized) {
    const side: FishSide = column.index % 2 === 0 ? "top" : "bottom";
    local.set(
      column.index,
      placeColumn(
        column,
        side,
        spineY,
        side === "top" ? topLength : bottomLength
      )
    );
  }
  const columns: PlacedColumn[] = [];
  let slotX = MARGIN;
  for (let slot = 0; slot * 2 < sized.length; slot++) {
    const members = [slot * 2, slot * 2 + 1].flatMap((i) => {
      const placed = local.get(i);
      return placed ? [placed] : [];
    });
    const join = slotX + Math.max(...members.map((placed) => -placed.left));
    for (const placed of members) {
      columns.push(shift(placed.column, join, 0));
    }
    slotX =
      join + Math.max(...members.map((placed) => placed.right)) + SLOT_GAP;
  }

  const headLines = wrapText(parse.effect || " ", HEAD_WRAP, HEAD_STYLE, 4);
  const headWidth =
    Math.ceil(maxTextWidth(headLines, HEAD_STYLE)) + BOX_PAD_X * 2;
  const headHeight =
    Math.ceil(headLines.length * HEAD_FONT * LINE_HEIGHT) + BOX_PAD_Y * 2 + 8;
  const spineEnd = Math.max(slotX, MARGIN + 60) + 6;
  const head: Rect = {
    x: spineEnd,
    y: spineY - headHeight / 2,
    width: headWidth,
    height: headHeight,
  };
  const width = head.x + head.width + MARGIN;
  const bottomExtent =
    Math.max(...bottoms.map((c) => c.boxHeight), 0) + bottomLength;
  const lowest = Math.max(spineY + bottomExtent, head.y + head.height);
  return {
    width: Math.ceil(width),
    height: Math.ceil(lowest + MARGIN),
    spineY,
    spineStart: MARGIN,
    spineEnd,
    head,
    headLines,
    columns,
  };
}

/** Width of a category name, for the tests that check boxes fit their words. */
export function categoryTextWidth(text: string): number {
  return estimateTextWidth(text, CATEGORY_STYLE);
}

/* ------------------------------------------------------- text alternative */

export interface FishRowText {
  category: string;
  level: 0 | 1;
  text: string;
}

export function fishRows(parse: FishParse): FishRowText[] {
  return parse.categories.flatMap((category) =>
    category.causes.length === 0
      ? [{ category: category.name, level: 0 as const, text: "" }]
      : category.causes.map((cause) => ({
          category: category.name,
          level: cause.level,
          text: cause.text,
        }))
  );
}

/** The diagram as the outline it was written from, two spaces per level. */
export function fishOutline(parse: FishParse): string {
  const lines = [parse.effect];
  for (const category of parse.categories) {
    lines.push(`  ${category.name}`);
    for (const cause of category.causes) {
      lines.push(`${cause.level === 0 ? "    " : "      "}${cause.text}`);
    }
  }
  return lines.join("\n");
}
